import {
  AlertsManagementFilterBaseColumns,
  AlertsManagementFilterModel,
} from '@gripp/shared-logic';
import {
  Colors,
  colors as CropCircleColors,
  FormContext,
  Layout,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { array, string, object as yupObject } from 'yup';

export type AlertsManagementFilterScreenHandle = {
  onReset: () => Promise<void>;
};

type AlertsManagementFilterScreenProps = {
  filter: AlertsManagementFilterModel | undefined;
  setFilter: (filter: any) => Promise<any>;
};

const formSchema = yupObject({
  columns: array().of(string().optional().nullable()).optional().nullable(),
  status: string().optional().nullable(),
});

export const AlertsManagementFilterScreen = forwardRef<
  AlertsManagementFilterScreenHandle,
  AlertsManagementFilterScreenProps
>(function AlertsManagementFilterScreen({ filter, setFilter }, ref) {
  const { t } = useTranslation();
  const slideLayout = useSlideLayoutSafe();
  const filterTitle = t('common.filter');

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      columns: (() => {
        if (!filter?.columns) return ['name'];
        const cols = Array.from(
          new Set(
            (filter.columns as any[]).filter(
              (c: any): c is string => c != null && typeof c === 'string'
            )
          )
        );
        return cols.includes('name') ? cols : ['name', ...cols];
      })(),
      status: filter?.status || '',
    },
  });

  const { handleSubmit, watch, reset } = formMethods;
  const onSubmitRef = useRef(onSubmit);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const subscription = watch(() => {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          handleSubmit(onSubmitRef.current)();
        }, 1000);
      });
      return () => {
        subscription.unsubscribe();
        clearTimeout(debounceTimerRef.current);
      };
    }
    return;
  }, [watch, handleSubmit]);

  useEffect(() => {
    slideLayout?.setColumnTitle(filterTitle);
    return () => slideLayout?.setColumnTitle(undefined);
  }, [slideLayout, filterTitle]);

  const createDefaultFilter = useCallback(
    () => new AlertsManagementFilterModel(),
    []
  );

  const resetForm = useCallback(() => {
    const defaultFilter = createDefaultFilter();
    let defaultColumns = defaultFilter.columns || ['name'];
    if (!defaultColumns.includes('name'))
      defaultColumns = ['name', ...defaultColumns];
    reset({ columns: defaultColumns, status: '' });
  }, [reset, createDefaultFilter]);

  useEffect(() => {
    if (!filter) resetForm();
  }, [filter, resetForm]);

  async function onSubmit(data: any) {
    if (data.columns) {
      let columns = Array.from(
        new Set(
          (data.columns as any[]).filter(
            (c: any): c is string => c != null && typeof c === 'string'
          )
        )
      );
      if (!columns.includes('name')) columns = ['name', ...columns];
      data.columns = columns;
    }
    if (!data.status) {
      data.status = undefined;
    }
    const filterModel = new AlertsManagementFilterModel({
      ...filter,
      ...data,
    });
    await setFilter(filterModel);
  }
  onSubmitRef.current = onSubmit;

  const onReset = useCallback(async () => {
    resetForm();
    const defaultFilter = createDefaultFilter();
    await setFilter(defaultFilter);
  }, [resetForm, setFilter, createDefaultFilter]);

  useImperativeHandle(ref, () => ({ onReset }), [onReset]);

  const canReset = (): boolean => {
    const formData = watch();
    const defaultFilter = createDefaultFilter();
    const columnsChanged =
      JSON.stringify(formData.columns?.sort()) !==
      JSON.stringify(defaultFilter.columns?.sort());
    const statusChanged = !!formData.status;
    return columnsChanged || statusChanged;
  };

  const baseColumns = AlertsManagementFilterBaseColumns;

  const statusOptions = [
    { value: '', label: t('alertsManagement.filter.status.all') },
    { value: 'ENABLED', label: t('alertsManagement.filter.status.enabled') },
    { value: 'DISABLED', label: t('alertsManagement.filter.status.disabled') },
  ];

  return (
    <FormContext.Provider value={formMethods as any}>
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
        >
          {/* STATUS SECTION */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t('alertsManagement.filter.status.label')}
            </Text>
            <Controller
              control={formMethods.control}
              name="status"
              render={({ field: { onChange, value } }) => (
                <View style={styles.optionsContainer}>
                  {statusOptions.map((option) => {
                    const isSelected = (value || '') === option.value;
                    return (
                      <Pressable
                        key={option.value}
                        onPress={() => onChange(option.value)}
                        style={[
                          styles.optionChip,
                          isSelected && styles.optionChipSelected,
                        ]}
                      >
                        <Text
                          style={[
                            styles.optionChipText,
                            isSelected && styles.optionChipTextSelected,
                          ]}
                        >
                          {option.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              )}
            />
          </View>

          {/* COLUMNS SECTION */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t('alertsManagement.filter.columnSelection')}
            </Text>
            <Controller
              control={formMethods.control}
              name="columns"
              render={({ field: { onChange, value } }) => {
                const currentValue = (value || []).filter(
                  (c): c is string => c != null
                );
                const selectableColumns = baseColumns.filter(
                  (col) => col !== 'name'
                );
                const onPressColumn = (column: string) => {
                  if (column === 'name') return;
                  let newValue: string[];
                  if (currentValue.includes(column))
                    newValue = currentValue.filter((c) => c !== column);
                  else newValue = [...currentValue, column];
                  if (!newValue.includes('name'))
                    newValue = ['name', ...newValue];
                  onChange(newValue);
                };
                const effectiveValue = currentValue.includes('name')
                  ? currentValue
                  : ['name', ...currentValue];
                return (
                  <View style={styles.optionsContainer}>
                    <Pressable
                      key="name"
                      disabled={true}
                      style={[
                        styles.optionChip,
                        styles.optionChipSelected,
                        styles.optionChipDisabled,
                      ]}
                    >
                      <Text
                        style={[
                          styles.optionChipText,
                          styles.optionChipTextSelected,
                        ]}
                      >
                        {t('alertsManagement.filter.columns.name')}
                      </Text>
                    </Pressable>
                    {selectableColumns.map((column) => {
                      const isSelected = effectiveValue.includes(column);
                      return (
                        <Pressable
                          key={column}
                          onPress={() => onPressColumn(column)}
                          style={[
                            styles.optionChip,
                            isSelected && styles.optionChipSelected,
                          ]}
                        >
                          <Text
                            style={[
                              styles.optionChipText,
                              isSelected && styles.optionChipTextSelected,
                            ]}
                          >
                            {t(`alertsManagement.filter.columns.${column}`)}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                );
              }}
            />
          </View>
        </ScrollView>
      </View>
    </FormContext.Provider>
  );
});

const styles = StyleSheet.create({
  container: {
    ...Layout.container,
    backgroundColor: Colors.white,
    flex: 1,
    position: 'relative',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 100,
  },
  section: {
    paddingTop: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: CropCircleColors.neutral[600],
    marginBottom: 12,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: CropCircleColors.neutral[200],
    gap: 6,
  },
  optionChipSelected: {
    backgroundColor: Colors.black,
    borderColor: Colors.black,
  },
  optionChipDisabled: {
    opacity: 0.6,
  },
  optionChipText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.black,
  },
  optionChipTextSelected: {
    color: Colors.white,
  },
});
